import { lazy, useState, useEffect } from "react";
import { GPLogo } from "../components/theme/logos";
import { MainTitle, SecondaryTitle } from "../data/style/Style";
import { DescriptionText, TwoTonedHeader } from "../components/theme/texts";

const Stack = lazy(() => import("@mui/material/Stack"));

const IMPACT = [
  {
    icon: "⚡",
    title: "New Features",
    desc: "Fund development of new workout types, screens, and analytics tools.",
  },
  {
    icon: "🐛",
    title: "Bug Fixes",
    desc: "Keep the app stable and polished across all devices and OS versions.",
  },
  {
    icon: "🖥️",
    title: "Web Platform",
    desc: "Help complete the web version so you can train from any device.",
  },
  {
    icon: "🔒",
    title: "Infrastructure",
    desc: "Cover server costs, database hosting, and security maintenance.",
  },
  {
    icon: "📱",
    title: "App Store Fees",
    desc: "Pay Apple & Google developer fees to keep the app publicly available.",
  },
  {
    icon: "💬",
    title: "Community",
    desc: "Invest in leaderboards, challenges, and social features for athletes.",
  },
];

const STATS = [
  { value: "1", label: "Developer", icon: "👤" },
  { value: "2+", label: "Years Building", icon: "📅" },
  { value: "26", label: "Screens Built", icon: "📱" },
  { value: "Free", label: "Forever", icon: "❤️" },
];

const AMOUNTS = [
  {
    value: 3,
    emoji: "☕",
    label: "Coffee",
    desc: "A coffee to keep the dev fuelled for the next session",
    link: process.env.REACT_APP_DONATE_COFFEE,
  },
  {
    value: 5,
    emoji: "🏋",
    label: "Workout",
    desc: "Cover a day of server costs and keep the app running",
    popular: true,
    link: process.env.REACT_APP_DONATE_WORKOUT,
  },
  {
    value: 10,
    emoji: "🚀",
    label: "Booster",
    desc: "Help accelerate a new feature or performance improvement",
    link: process.env.REACT_APP_DONATE_BOOSTER,
  },
  {
    value: 20,
    emoji: "🏆",
    label: "Champion",
    desc: "Significant support that funds a whole week of development",
    link: process.env.REACT_APP_DONATE_CHAMPION,
  },
];

const Donate = () => {
  useGlobalStyles();
  const [selected, setSelected] = useState(3);

  let donateLink = AMOUNTS.filter((item) => item.value === selected)[0].link;

  const handleAmountClick = (val) => setSelected(val);

  return (
    <Stack
      sx={{
        minHeight: "100vh",
        paddingTop: 5,
        paddingLeft: 5,
        paddingRight: 5,
      }}
    >
      {/* Ambient top glow */}
      <div
        style={{
          position: "fixed",
          top: -150,
          left: "50%",
          transform: "translateX(-50%)",
          width: 800,
          height: 540,
          borderRadius: "50%",
          background:
            "radial-gradient(ellipse,rgba(249,115,22,.14) 0%,rgba(249,115,22,.05) 40%,transparent 70%)",
          pointerEvents: "none",
          zIndex: 0,
          animation: "gpAmbient 4s ease-in-out infinite alternate",
        }}
      />
      <div
        style={{
          position: "fixed",
          bottom: -160,
          right: -80,
          width: 420,
          height: 420,
          borderRadius: "50%",
          background:
            "radial-gradient(ellipse,rgba(249,115,22,.07) 0%,transparent 65%)",
          pointerEvents: "none",
          zIndex: 0,
        }}
      />

      {/* Grain */}
      <div
        style={{
          position: "fixed",
          inset: 0,
          opacity: 0.18,
          zIndex: 0,
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.9' numOctaves='4'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='.04'/%3E%3C/svg%3E\")",
          backgroundSize: "200px 200px",
          pointerEvents: "none",
        }}
      />
      <Stack direction={"row"} alignItems={"center"} gap={2}>
        <GPLogo size={40} />
        <MainTitle fontSize={"1.1rem"}>
          Grind<span style={{ color: "#f97316" }}>&nbsp;Partner</span>
        </MainTitle>
      </Stack>
      {/* ── Main content ── */}
      <main
        style={{
          maxWidth: 1200,
          margin: "0 auto",
          padding: "0 clamp(16px,4vw,40px) 64px",
        }}
      >
        {/* ── Hero ── */}
        <div
          className="gp-fade-1"
          style={{ textAlign: "center", marginBottom: 48 }}
        >
          {/* Heartbeat icon */}
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 72,
              height: 72,
              borderRadius: "50%",
              marginBottom: 20,
              background:
                "radial-gradient(circle,rgba(249,115,22,.15) 0%,rgba(249,115,22,.04) 60%,transparent 100%)",
              border: "1px solid rgba(249,115,22,.25)",
              fontSize: 34,
              animation: "gpHeartbeat 2.4s ease-in-out infinite",
            }}
          >
            ❤️
          </div>
          <div
            style={{
              alignItems: "center",
              gap: 7,
              padding: "5px 14px",
              borderRadius: 99,
              marginBottom: 16,
              background: "rgba(249,115,22,.08)",
              border: "1px solid rgba(249,115,22,.22)",
              fontFamily: "DM Sans, sans-serif",
              fontSize: 11,
              fontWeight: 700,
              color: "#f97316",
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              display: "flex",
            }}
          >
            <span>🙏</span> Support the Developer
          </div>
          <TwoTonedHeader
            fontSize={"clamp(40px,6vw,74px)"}
            mainText={"Support"}
            subText={"Grind Partner"}
          />
          <DescriptionText
            style={{
              maxWidth: 900,
              margin: "0 auto",
              lineHeight: 1.8,
              fontSize: "clamp(14px,1.5vw,17px)",
            }}
          >
            Grind Partner is built and maintained by a{" "}
            <strong style={{ color: "#b0b0b0" }}>single developer</strong> with
            limited resources. Every feature, update, and improvement comes from
            countless hours of dedication. Your support is crucial — even a
            small contribution makes a real difference.
          </DescriptionText>
        </div>
        {/* ── Stats strip ── */}
        <div
          className="gp-fade-2 gp-stats-grid"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(4,1fr)",
            gap: 10,
            marginBottom: 48,
          }}
        >
          {STATS.map((stat, i) => (
            <StatItem key={i} stat={stat} />
          ))}
        </div>
        {/* ── Amount selector ── */}
        <div className="gp-fade-3" style={{ marginBottom: 32 }}>
          <SecondaryTitle marginBottom={3} textAlign={"center"}>
            Choose Your Contribution
          </SecondaryTitle>
          {/* Amount cards grid */}
          <div
            className="gp-amounts-grid"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(4,1fr)",
              gap: 12,
              marginBottom: 16,
            }}
          >
            {AMOUNTS.map((a) => (
              <AmountCard
                key={a.value}
                amount={a}
                selected={selected === a.value}
                onClick={handleAmountClick}
              />
            ))}
          </div>
        </div>
        {/* ── CTA ── */}
        <div className="gp-fade-4" style={{ marginBottom: 52 }}>
          {/* Summary pill */}
          {selected > 0 && (
            <DescriptionText
              style={{ textAlign: "center", fontSize: "0.8rem" }}
            >
              You're donating{" "}
              <span
                style={{
                  fontFamily: "Oswald, sans-serif",
                  fontWeight: 700,
                  fontSize: 20,
                  color: "#f97316",
                }}
              >
                {selected}€
              </span>{" "}
              — thank you! 🙏
            </DescriptionText>
          )}
          <a
            href={donateLink}
            rel="noreferrer"
            target="_blank"
            disabled={selected <= 0}
            className="gp-btn"
            style={{
              width: "100%",
              padding: "16px",
              borderRadius: 16,
              border: "none",
              textDecoration: "none",
              background:
                selected > 0
                  ? "linear-gradient(135deg,#f97316,#ea580c)"
                  : "#161616",
              fontFamily: "Oswald, sans-serif",
              fontWeight: 700,
              fontSize: 18,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              color: selected > 0 ? "#fff" : "#333",
              boxShadow:
                selected > 0 ? "0 6px 28px rgba(249,115,22,.42)" : "none",
              transition: "all .22s",
              cursor: selected > 0 ? "pointer" : "not-allowed",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 10,
            }}
          >
            ❤️ Donate {selected > 0 ? `${selected}€` : "Now"}
          </a>
          <DescriptionText
            style={{ marginTop: 2, textAlign: "center", fontSize: "0.7rem" }}
          >
            <span>🔒</span> Secure payment · No account required · Cancel
            anytime
          </DescriptionText>
        </div>
        {/* ── What your support funds ── */}
        <div className="gp-fade-5">
          <div style={{ textAlign: "center", marginBottom: 20 }}>
            <SecondaryTitle>Where your money goes</SecondaryTitle>
            <MainTitle fontSize={"clamp(20px,3vw,28px)"}>
              What Your Support Funds
            </MainTitle>
          </div>
          <div
            className="gp-impact-grid"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3,1fr)",
              gap: 10,
            }}
          >
            {IMPACT.map((item, i) => (
              <ImpactCard key={i} item={item} index={i} />
            ))}
          </div>
        </div>

        {/* ── Developer note ── */}
        <div
          className="gp-fade-6"
          style={{
            marginTop: 40,
            padding: "24px 28px",
            borderRadius: 20,
            background: "#111",
            border: "1px solid #1e1e1e",
            position: "relative",
            overflow: "hidden",
          }}
        >
          {/* Stripe */}
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: 2,
              background:
                "linear-gradient(90deg,#f97316,#ea580c,rgba(249,115,22,.04))",
            }}
          />
          <div style={{ display: "flex", gap: 18, flexWrap: "wrap" }}>
            <GPLogo size={56} />
            <div style={{ flex: 1, minWidth: 200 }}>
              <MainTitle marginBottom={2} fontSize={"1rem"}>
                A Note from the Developer
              </MainTitle>
              <DescriptionText style={{ fontSize: "0.9rem" }}>
                Building Grind Partner has been a passion project from day one.
                Every line of code, every screen, and every feature was crafted
                with care. Your donation — no matter how small — tells me that
                what I'm building matters. Thank you for being part of this
                journey. 🙏
              </DescriptionText>
              <div
                style={{
                  marginTop: 12,
                  fontFamily: "Oswald, sans-serif",
                  fontWeight: 700,
                  fontSize: 14,
                  color: "#f97316",
                }}
              >
                — The Grind Partner Developer
              </div>
            </div>
          </div>
        </div>
      </main>
    </Stack>
  );
};

export default Donate;
// ─────────────────────────────────────────
// Inject global styles + fonts
// ─────────────────────────────────────────
function useGlobalStyles() {
  useEffect(() => {
    if (document.getElementById("gp-donate-kf")) return;
    const s = document.createElement("style");
    s.id = "gp-donate-kf";
    s.textContent = `
      @import url('https://fonts.googleapis.com/css2?family=Oswald:wght@400;600;700;900&family=DM+Sans:wght@400;500;600;700&display=swap');
      *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
      body { background: #0a0a0a; }
      ::-webkit-scrollbar { width: 4px; }
      ::-webkit-scrollbar-thumb { background: #f97316; border-radius: 2px; }

      @keyframes gpAmbient  { from{opacity:.55;transform:translateX(-50%) scale(1)} to{opacity:1;transform:translateX(-50%) scale(1.1)} }
      @keyframes gpFadeUp   { from{opacity:0;transform:translateY(22px)} to{opacity:1;transform:translateY(0)} }
      @keyframes gpHeartbeat{ 0%,100%{transform:scale(1)} 14%{transform:scale(1.22)} 28%{transform:scale(1)} 42%{transform:scale(1.16)} 70%{transform:scale(1)} }
      @keyframes gpPop      { 0%{transform:scale(.5);opacity:0} 60%{transform:scale(1.15)} 100%{transform:scale(1);opacity:1} }

      .gp-fade-0 { animation: gpFadeUp .65s ease .05s both; }
      .gp-fade-1 { animation: gpFadeUp .65s ease .15s both; }
      .gp-fade-2 { animation: gpFadeUp .65s ease .28s both; }
      .gp-fade-3 { animation: gpFadeUp .65s ease .40s both; }
      .gp-fade-4 { animation: gpFadeUp .65s ease .52s both; }
      .gp-fade-5 { animation: gpFadeUp .65s ease .64s both; }
      .gp-fade-6 { animation: gpFadeUp .65s ease .76s both; }

      .gp-link { color: #f97316; text-decoration: none; font-weight: 700; transition: opacity .15s; }
      .gp-link:hover { opacity: .72; }

      .gp-amount-card {
        transition: all .22s cubic-bezier(.34,1.56,.64,1);
        cursor: pointer;
      }
      .gp-amount-card:hover { transform: translateY(-4px) scale(1.02); }
      .gp-amount-card:active { transform: translateY(-1px) scale(.99); }

      .gp-btn {
        transition: all .22s ease; cursor: pointer;
      }
      .gp-btn:hover { transform: translateY(-2px); }
      .gp-btn:active { transform: translateY(0); }

      .gp-custom-input:focus { outline: none; }
      .gp-custom-input::placeholder { color: #333; }

      @media (max-width: 640px) {
        .gp-amounts-grid { grid-template-columns: 1fr 1fr !important; }
        .gp-impact-grid  { grid-template-columns: 1fr !important; }
        .gp-stats-grid   { grid-template-columns: 1fr 1fr !important; }
      }
      @media (max-width: 380px) {
        .gp-amounts-grid { grid-template-columns: 1fr !important; }
      }
    `;
    document.head.appendChild(s);
  }, []);
}

// ─────────────────────────────────────────
// Stat item
// ─────────────────────────────────────────
function StatItem({ stat }) {
  return (
    <Stack
      alignItems={"center"}
      gap={1}
      textAlign={"center"}
      style={{
        padding: "16px 12px",
        borderRadius: 14,
        background: "#111",
        border: "1px solid #1e1e1e",
      }}
    >
      <div style={{ fontSize: 22 }}>{stat.icon}</div>
      <div
        style={{
          fontFamily: "Oswald, sans-serif",
          fontWeight: 900,
          fontSize: "clamp(22px,3vw,30px)",
          color: "#f97316",
          lineHeight: 1,
        }}
      >
        {stat.value}
      </div>
      <div
        style={{
          fontFamily: "DM Sans, sans-serif",
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: "0.12em",
          textTransform: "uppercase",
          color: "#555",
        }}
      >
        {stat.label}
      </div>
    </Stack>
  );
}

// ─────────────────────────────────────────
// Amount card
// ─────────────────────────────────────────
function AmountCard({ amount, selected, onClick }) {
  return (
    <Stack
      className="gp-amount-card"
      onClick={() => onClick(amount.value)}
      style={{
        position: "relative",
        padding: "22px 16px",
        borderRadius: 18,
        border: "1.5px solid",
        borderColor: selected
          ? "#f97316"
          : amount.popular
            ? "rgba(249,115,22,.3)"
            : "#1e1e1e",
        background: selected
          ? "linear-gradient(160deg,rgba(249,115,22,.14) 0%,rgba(249,115,22,.05) 60%,transparent 100%)"
          : amount.popular
            ? "linear-gradient(160deg,rgba(249,115,22,.07) 0%,#111 60%)"
            : "#111",
        boxShadow: selected
          ? "0 0 0 3px rgba(249,115,22,.18), 0 8px 32px rgba(249,115,22,.2)"
          : amount.popular
            ? "0 4px 20px rgba(249,115,22,.1)"
            : "none",
        flexDirection: "column",
        alignItems: "center",
        gap: 8,
        textAlign: "center",
        userSelect: "none",
      }}
    >
      {/* Popular badge */}
      {amount.popular && (
        <div
          style={{
            position: "absolute",
            top: -11,
            left: "50%",
            transform: "translateX(-50%)",
            padding: "3px 12px",
            borderRadius: 99,
            background: "linear-gradient(135deg,#f97316,#ea580c)",
            fontFamily: "Oswald, sans-serif",
            fontWeight: 700,
            fontSize: 10,
            color: "#fff",
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            whiteSpace: "nowrap",
            boxShadow: "0 2px 10px rgba(249,115,22,.4)",
          }}
        >
          ⭐ Most Popular
        </div>
      )}

      {/* Emoji */}
      <div
        style={{
          width: 48,
          height: 48,
          borderRadius: 14,
          fontSize: 24,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: selected ? "rgba(249,115,22,.15)" : "#161616",
          border: `1px solid ${selected ? "rgba(249,115,22,.3)" : "#222"}`,
          transition: "all .2s",
        }}
      >
        {amount.emoji}
      </div>

      {/* Amount */}
      <div
        style={{
          fontFamily: "Oswald, sans-serif",
          fontWeight: 900,
          fontSize: "clamp(30px,4vw,40px)",
          lineHeight: 1,
          color: selected ? "#f97316" : amount.popular ? "#f97316" : "#f5f5f5",
          letterSpacing: "-0.01em",
          transition: "color .2s",
        }}
      >
        {amount.value}€
      </div>

      {/* Label */}
      <div
        style={{
          fontFamily: "Oswald, sans-serif",
          fontWeight: 700,
          fontSize: 12,
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          color: selected ? "#f97316" : "#555",
          transition: "color .2s",
        }}
      >
        {amount.label}
      </div>

      {/* Desc */}
      <DescriptionText size={"0.8rem"}>{amount.desc}</DescriptionText>

      {/* Selected check */}
      {selected && (
        <div
          style={{
            position: "absolute",
            top: 10,
            right: 12,
            width: 20,
            height: 20,
            borderRadius: "50%",
            background: "#f97316",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 11,
            color: "#fff",
            fontWeight: 900,
            animation: "gpPop .3s cubic-bezier(.34,1.56,.64,1) both",
          }}
        >
          ✓
        </div>
      )}
    </Stack>
  );
}

// ─────────────────────────────────────────
// Impact card
// ─────────────────────────────────────────
function ImpactCard({ item, index }) {
  return (
    <Stack
      style={{
        gap: 12,
        padding: "14px 16px",
        flexDirection: "row",
        borderRadius: 14,
        background: "#111",
        border: "1px solid #1e1e1e",
        animation: `gpFadeUp .6s ease ${0.3 + index * 0.08}s both`,
      }}
    >
      <Stack
        style={{
          width: 36,
          height: 36,
          borderRadius: 10,
          flexShrink: 0,
          background: "rgba(249,115,22,.1)",
          border: "1px solid rgba(249,115,22,.2)",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 17,
        }}
      >
        {item.icon}
      </Stack>
      <div>
        <MainTitle fontSize={"1rem"}>{item.title}</MainTitle>
        <DescriptionText style={{ fontSize: "0.8rem" }}>
          {item.desc}
        </DescriptionText>
      </div>
    </Stack>
  );
}
